/**
 * Phase 9 / Plan 09-01 — Centralised copy module for the
 * "We Set It Up For You" paid setup-service add-on (£249 one-off).
 *
 * Source of truth — all 7 surfaces import from this module so the locked
 * SPEC.md strings stay byte-identical across pricing card, homepage block,
 * checkout option, post-purchase banner, settings/billing card, onboarding
 * checklist, and intake form.
 *
 * LOCKED upstream (do not paraphrase):
 * - Service name: "We Set It Up For You"
 * - Price: "£249" / "£249 one-off"
 * - 7 sectors in the locked order (UI-SPEC field 4)
 * - BANNED_PHRASES list (UI-SPEC "Words / phrases banned in this phase")
 * - Intake submit success toast (CONTEXT.md D-07)
 *
 * Strings sourced verbatim from:
 *   .planning/phases/09-paid-setup-service-add-on-we-set-it-up-for-you-front-end-onl/SPEC.md (Copy pack)
 *   .planning/phases/09-paid-setup-service-add-on-we-set-it-up-for-you-front-end-onl/09-UI-SPEC.md (Copywriting Contract)
 */

// ─────────────────────────────────────────────────────────────────────────
// Atomic constants — referenced everywhere
// ─────────────────────────────────────────────────────────────────────────

export const SETUP_SERVICE_NAME = "We Set It Up For You" as const;
export const SETUP_SERVICE_PRICE_DISPLAY = "£249" as const;
export const SETUP_SERVICE_PRICE_NUMERIC = 249 as const;
export const SETUP_SERVICE_PRICE_PERIOD = "one-off" as const;
export const SETUP_SERVICE_TURNAROUND = "Delivered within 2 working days" as const;

/**
 * Recipient mailbox for the intake handoff email (Plan 09-05).
 * Domain matches the verified Resend domain per CONTEXT.md.
 */
export const SETUP_SERVICE_OPS_EMAIL = "hello@thesitebook.co.uk" as const;

// ─────────────────────────────────────────────────────────────────────────
// Sector list — LOCKED order per UI-SPEC field 4. Zod validates these
// 7 values at the API edge (researcher A3).
// ─────────────────────────────────────────────────────────────────────────

export const SECTORS = [
  "Domestic",
  "Commercial",
  "Civils",
  "Industrial",
  "Refurbishment",
  "Demolition",
  "Other",
] as const;

export type Sector = (typeof SECTORS)[number];

// ─────────────────────────────────────────────────────────────────────────
// Surface 1 — Pricing page add-on card (SPEC.md "Pricing page version")
// ─────────────────────────────────────────────────────────────────────────

export const PRICING_CARD = {
  heading: "We Set It Up For You",
  body: "Too busy to do the first one yourself? The £249 setup service covers the Site Book paperwork for this job.",
  bullets: [
    "company setup",
    "first project setup",
    "job paperwork pack prepared",
    "one amendment pass",
    "delivered within 2 working days",
  ],
  cta: "Book setup",
} as const;

// ─────────────────────────────────────────────────────────────────────────
// Surface 2 — Homepage promo block (SPEC.md "Homepage version")
// ─────────────────────────────────────────────────────────────────────────

export const HOMEPAGE_BLOCK = {
  heading: "Too busy to set it up yourself?",
  body: "Send us your details and we'll set up your company, create your first project, and prepare the Site Book paperwork for that job.",
  bullets: [
    "covers this job's paperwork",
    "delivered within 2 working days",
    "ideal if you are under pressure",
  ],
  cta: "Get started with setup",
} as const;

// ─────────────────────────────────────────────────────────────────────────
// Surface 3 — Checkout add-on row (SPEC.md "Checkout version")
// ─────────────────────────────────────────────────────────────────────────

export const CHECKOUT_OPTION = {
  label: "Book We Set It Up For You for £249",
  body: "Ideal if you want the Site Book paperwork for this job set up for you.",
  microcopy: "Delivered within 2 working days.",
} as const;

// ─────────────────────────────────────────────────────────────────────────
// Surface 4 — Post-purchase confirmation banner (UI-SPEC §Component 4)
// ─────────────────────────────────────────────────────────────────────────

export const POST_PURCHASE_BANNER = {
  heading: "You've added We Set It Up For You",
  body: "We'll use your details to set up your company, first project, and the Site Book paperwork for this job. We aim to deliver this within 2 working days.",
  nextStepPrompt: "We may need a few details from you to get started.",
  cta: "Send your details now",
  secondaryLink: "Or do this later from your dashboard",
} as const;

// ─────────────────────────────────────────────────────────────────────────
// Surface 5 — Settings/billing card (UI-SPEC §Component 5)
// Body re-uses PRICING_CARD.body so the strings stay byte-identical.
// ─────────────────────────────────────────────────────────────────────────

export const SETTINGS_BILLING_CARD = {
  heading: "We Set It Up For You",
  body: PRICING_CARD.body,
  bulletsCompact: "company setup · first project setup · job paperwork pack prepared",
  ctaDefault: "Buy setup service £249",
  ctaPurchased: "Setup service requested",
  ctaSubmitted: "We're on it — expect to hear from us within 2 working days.",
} as const;

// ─────────────────────────────────────────────────────────────────────────
// Surface 6 — Onboarding checklist item (UI-SPEC §Component 6)
// ─────────────────────────────────────────────────────────────────────────

export const ONBOARDING_CHECKLIST = {
  title: "Send us your business details",
  helper: "We need a few details to get started — sector, company name, lead contact.",
  ctaPrimary: "Send my details",
  ctaSecondary: "Dismiss",
  completedLabel: "Setup details sent — we'll be in touch within 2 working days.",
} as const;

// ─────────────────────────────────────────────────────────────────────────
// Surface 7 — Intake form (UI-SPEC §Component 7)
// ─────────────────────────────────────────────────────────────────────────

export const INTAKE_FORM = {
  pageHeading: "Tell us about your business",
  pageIntro:
    "We'll use this to set up your company, first project, and the Site Book paperwork for this job.",
  fieldLabels: {
    companyName: "Company name",
    projectName: "Primary project name / site",
    leadContactName: "Lead contact name",
    leadContactPhone: "Lead contact phone",
    sector: "Sector",
    additionalNotes: "Anything we should know",
    // Phase 11 / Plan 11-02 — optional site address (≤500 chars).
    siteAddress: "Site address (optional)",
  },
  placeholders: {
    companyName: "e.g. Smith Construction Ltd",
    projectName: "e.g. Rear extension, 14 Oak Lane",
    leadContactName: "Your name",
    leadContactPhone: "07xxx xxx xxx",
    sectorChoose: "Choose...",
    additionalNotes:
      "Site access constraints, tight deadlines, anything that would help us prep…",
  },
  submitLabel: "Send my details",
  submitting: "Sending…",
  cancelLabel: "Cancel",
  successToast: "Got it — we'll be in touch within 2 working days.",
  failureToast:
    "Couldn't send your details — please try again or email hello@thesitebook.co.uk",
  pendingReviewHeading: "Your details are saved",
  pendingReviewBody:
    "Our team is checking your billing before starting Pro access. You don't need to submit the form again; we'll be in touch.",
  notificationPendingBody:
    "Your details are saved, but we couldn't confirm that our team received the billing review request. Please retry notifying the team. Your details will stay filled in.",
  retryNotificationLabel: "Retry notifying the team",
} as const;

// ─────────────────────────────────────────────────────────────────────────
// Error states (UI-SPEC "Empty / Error / Confirmation states")
// ─────────────────────────────────────────────────────────────────────────

export const ERROR_COPY = {
  addonUnavailable:
    "This setup service is temporarily unavailable. Please continue without it or contact us.",
  intakeServerError: "Something went wrong. Please try again.",
} as const;

// ─────────────────────────────────────────────────────────────────────────
// Banned phrases — used by the Wave 4 banned-phrase scan (Plan 09-08).
// Any reviewer surfacing these in code or copy MUST block the PR.
// Kept exhaustive per UI-SPEC "Words / phrases banned in this phase".
// ─────────────────────────────────────────────────────────────────────────

export const BANNED_PHRASES = [
  "match your templates",
  "migrate your existing templates",
  "guarantee compliance",
  "approved by a consultant",
  "bespoke consultancy",
  "consultancy",
  "unlimited changes",
  "unlimited rewrites",
  "managed onboarding",
  "implementation package",
  "concierge setup",
  "done-for-you compliance",
  "Seamless implementation",
  "Expert-led onboarding experience",
  "Tailored document migration workflow",
  "VAT",
  "ex VAT",
  "+VAT",
] as const;

// ─────────────────────────────────────────────────────────────────────────
// Phase 11 additions — service-first funnel redesign
//
// All values below are LOCKED via Phase 11 CONTEXT.md "Defaults locked for
// autonomous execution" (D-A4, D-A5) + the locked refund + email defaults.
// Plans 11-02 and 11-04 import these constants — never re-define them
// inline at a callsite.
// ─────────────────────────────────────────────────────────────────────────

/** D-A5 — trial starts on intake submission, runs 30 days. */
export const SETUP_SERVICE_TRIAL_DAYS = 30 as const;

/** Default 3 — Slack channel name for ops orders feed. */
export const SLACK_SETUP_SERVICE_CHANNEL_NAME = "#setup-service-orders" as const;

/**
 * Default 5 — refund policy copy shown on the intake confirmation /
 * post-payment page. Three locked terms; do not paraphrase or reorder.
 */
export const REFUND_POLICY_COPY = {
  heading: "Refund policy",
  body: [
    "7 days from intake submission, before we begin work — full refund.",
    "After work has begun, no refund.",
    "If we miss the 2 working day SLA — full refund.",
  ],
} as const;

/**
 * Default 4 — trial-warning email when five days or less remain. The daily
 * job retries an unaccepted send until the trial ends, so its wording must
 * remain true throughout that window.
 * Plan 11-04 (setup-service-trial-warnings cron) imports this and renders
 * the buyer's first name + signs from no-reply@thesitebook.co.uk.
 */
export const TRIAL_WARNING_EMAIL = {
  subject: "Your Pro trial is ending soon",
  heading: "Your Pro trial is ending soon",
  body: "Your 30-day Pro trial is ending soon. Keep all your projects on Pro for £39/mo, or stay on free — we'll preserve your setup project either way.",
  ctaLabel: "Keep Pro for £39/mo",
  ctaPath: "/settings?tab=billing",
} as const;

/**
 * Default 4 — trial-lapsed email fired at proTrialEndsAt.
 * D-A6 — the setup project keeps Pro-quality docs forever via the
 * setupServicePurchased flag, even after this email lands.
 */
export const TRIAL_LAPSED_EMAIL = {
  subject: "Your Pro trial has ended",
  heading: "Your Pro trial has ended",
  body: "Your setup project keeps Pro-quality docs forever. Other projects are now on the free tier (watermarks return, sign-off + share locked). Re-enable Pro any time from Settings.",
  ctaLabel: "Re-enable Pro",
  ctaPath: "/settings?tab=billing",
} as const;

/**
 * Default 5 — +24h nudge email when buyer paid but never submitted
 * intake. Plan 11-02 ships the cron that fires this.
 * The magic-link path is interpolated at send time per buyer.
 */
export const INTAKE_NUDGE_EMAIL = {
  subject: "Finish your setup — quick details to start the 2-day clock",
  heading: "We've got your payment — we just need a few details",
  body: "We can't start the 2-day clock until you tell us about your project. It takes 90 seconds. Click below to fill in the intake form.",
  ctaLabel: "Open intake form",
} as const;
